<template>
  <DialogElement
    ref="dialog"
    :closed-by="'any'"
    :aria-label="t('dictionaryModal.componentTitle')"
    class="dictionary flex absolute justify-center"
    @close="definition?.clear()"
  >
    <div class="details keep-dialog w-full">
      <p v-if="false /* not used */ && excerpt" class="excerpt">
        {{ excerpt.before }}<strong>{{ excerpt.word }}</strong
        >{{ excerpt.after }}
      </p>
      <div
        v-for="(detail, index) in definition?.details"
        :key="index"
        class="definition mb-12"
      >
        <h2
          class="relative flex items-baseline text-2xl font-bold uppercase"
          tabindex="0"
        >
          {{ detail?.title }}
          <span class="type"
            >({{ t(`dictionaryModal.synt.${detail.type}`, detail.type) }})</span
          >
          <ReadSelectionCommand
            v-slot="btnProps"
            :selection="{ text: detail?.title }"
          >
            <SecondaryMenuButton
              class="self-center ms-auto"
              v-bind="btnProps"
            />
          </ReadSelectionCommand>
        </h2>
        <img
          v-if="detail.image"
          :src="detail.image"
          :alt="'Image related to ' + detail?.title"
          class="object-cover"
        />
        <p class="description w-full">{{ detail.description }}</p>
        <p v-if="detail.synonyms?.length" class="synonyms w-full">
          {{ t("dictionaryModal.synonyms", detail.synonyms.length) }}:
          {{ detail.synonyms.join(", ") }}
        </p>
      </div>
    </div>
    <CloseDialogButton class="absolute close-sidebar" />
  </DialogElement>
</template>
<script setup lang="ts">
import { computed, useTemplateRef, watchEffect } from "vue";
import { useElementBounding, useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import { computePosition, flip, offset, shift } from "@floating-ui/dom";

import { useAttributesStore } from "@/stores/attributes";

import DialogElement from "../shared/DialogElement.vue";
import CloseDialogButton from "../shared/CloseDialogButton.vue";
import { WordDefinition } from "../navigator/types";

import ReadSelectionCommand from "@/commands/tts/ReadSelectionCommand.vue";
import SecondaryMenuButton from "./SecondaryMenuButton.vue";

const props = defineProps<{
  definition: WordDefinition;
}>();

const dialog = useTemplateRef<InstanceType<typeof DialogElement>>("dialog");

const MAX_EXCERPT_LENGTH = 25;

const excerpt = computed<{
  before: string;
  word: string;
  after: string;
} | null>(() => {
  if (!props.definition) return null;
  let { before, word, after } = props.definition.context;
  before =
    "…" + before.slice(-MAX_EXCERPT_LENGTH).split(" ").slice(1).join(" ");
  after =
    after.slice(0, MAX_EXCERPT_LENGTH).split(" ").slice(0, -1).join(" ") + "…";

  return { before, word, after };
});

const { t } = useI18n();

const attributesStore = useAttributesStore();

const { width: windowWidth, height: windowHeight } = useWindowSize();
const { width: modalWidth, height: modalHeight } = useElementBounding(
  () => dialog.value?.$el as HTMLDialogElement
);

/* This effect repositions the modal based on the word's coordinates */
watchEffect(async () => {
  const el = dialog.value?.$el as HTMLDialogElement;
  if (!el || attributesStore.isMobile) return;
  if (!props.definition) return;

  const coord = props.definition.coordinates;
  // Center the modal in screen if no coordinates are provided
  if (!coord) {
    const definitionCenterX = windowWidth.value / 2 - modalWidth.value / 2;
    const definitionTopY = windowHeight.value / 2 - modalHeight.value / 2;

    el.style.left = `${Math.round(definitionCenterX)}px`;
    el.style.top = `${Math.round(definitionTopY)}px`;
    return;
  }

  const boundingRect = new DOMRect(coord.x, coord.y, coord.width, coord.height);
  const position = await computePosition(
    {
      getBoundingClientRect: () => boundingRect,
    },
    el,
    {
      placement: "right",
      middleware: [
        offset(25), // space between selection and menu
        flip(),
        shift(),
      ],
    }
  );

  el.style.left = position.x + "px";
  el.style.top = position.y + "px";
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/variables.scss" as *;
.dictionary {
  z-index: 3;
  overflow: hidden;
  top: auto;
  bottom: 0;
  width: 100vw;
  height: auto;
  max-width: unset;
  max-height: 100%;
  padding: $spacingXL 0 0;
  margin: 0;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 2px solid rgba(158, 158, 158, 0.2);
  @media (min-width: $screenSM) {
    bottom: auto;
    border-radius: $spacingM;
    max-width: min(100vw, $screenS);
    max-height: min(80vh, var(--safe-height));
  }
  &:focus {
    outline: 0;
  }
}
.details {
  overflow-y: auto;
  padding: 0 $spacingL;

  @media (min-width: $screenSM) {
    padding: 0 $spacingXL;
  }
}
.definition {
  & > * {
    margin: $spacingM 0;
    @media (min-width: $screenSM) {
      margin: $spacingL 0;
    }
  }
  & > h2 {
    margin: $spacingM 0 $spacingL;
    @media (min-width: $screenSM) {
      margin: $spacingM 0 $spacingXL;
    }
  }

  img {
    margin: auto;
  }
  p {
    // TODO: why is it used only here?
    font-family: "Nunito", sans-serif;
    background-color: white;
    color: black;
  }
}
h2 {
  text-transform: uppercase;
  &:before {
    content: "";
    position: absolute;
    width: calc(100% + $spacingL);
    height: 2px;
    display: block;
    bottom: 0;
    background-color: var(--DM__theme-font-color);
    @media (min-width: $screenSM) {
      width: 100%;
    }
  }
  &:focus {
    outline: 2px solid var(--DM__theme-border-color);
  }
  &:focus:not(:focus-visible) {
    outline-color: transparent;
  }
}
.type {
  font-size: 1.1rem;
  line-height: 1.1rem;
  font-weight: normal;
  font-style: italic;
  text-transform: none;
  color: var(--DM__theme-font-color);
}
.excerpt,
.synonyms {
  font-style: italic;
}
.object-cover {
  border: 1px solid #d4d4d4;
  border-radius: 8px;
  max-width: 350px;
  max-height: 350px;
  object-fit: cover;
}

.close-sidebar {
  left: auto;
  right: $spacingM;
}
</style>
